<template>
  <div class="address-selector">
    <div class="address-input-row">
      <el-input
        v-model="currentAddress"
        :placeholder="placeholder"
        maxlength="100"
        @input="onAddressInput">
        <template #append>
          <el-button
            icon="el-icon-location-outline"
            title="打开地图选择位置"
            @click="openMapSelector">
          </el-button>
        </template>
      </el-input>
      <AddressLibrarySelector
        v-if="libraryEnabled"
        class="address-library-selector-action"
        :site-id="siteId"
        @select="handleLibrarySelect">
      </AddressLibrarySelector>
    </div>

    <!-- 嵌入的地图组件 -->
    <div v-if="showMap" class="map-container">
      <div class="map-header">
        <span class="map-title">地图位置选择</span>
        <el-button
          size="mini"
          type="text"
          icon="el-icon-close"
          @click="closeMap">
        </el-button>
      </div>
      <div class="map-content">
        <div class="search-box">
          <el-input
            v-model="searchKeyword"
            placeholder="搜索地点"
            size="mini"
            @keyup.enter="searchLocation">
            <template #append>
              <el-button
                size="mini"
                type="primary"
                icon="el-icon-search"
                @click="searchLocation">
              </el-button>
            </template>
          </el-input>
        </div>
        <div :id="mapContainerId" class="map-view"></div>
        <div class="map-info">
          <div class="selected-position" v-if="selectedPosition">
            <span class="label">选中位置：</span>
            <span class="position">{{selectedPosition}}</span>
          </div>
          <div class="selected-address">
            <span class="label">选中地址：</span>
            <span class="address">{{selectedAddress || '请在地图上点击选择位置'}}</span>
          </div>
          <div class="coordinates" v-if="selectedCoordinates">
            <span class="label">坐标：</span>
            <span class="coords">{{selectedCoordinates}}</span>
          </div>
        </div>
      </div>
      <div class="map-footer">
        <el-button
          v-if="libraryEnabled"
          size="mini"
          type="primary"
          plain
          icon="el-icon-folder-add"
          :disabled="!siteId || !selectedPosition || !selectedCoordinates"
          @click="saveToLibrary">
          保存到地址库
        </el-button>
        <el-button
          size="mini"
          type="primary"
          :disabled="!selectedPosition || !selectedCoordinates"
          @click="confirmSelection">
          确认选择
        </el-button>
        <el-button
          size="mini"
          @click="closeMap">
          取消
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import AddressLibrarySelector from './AddressLibrarySelector'

export default {
  name: 'AddressSelector',
  components: {
    AddressLibrarySelector
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    coordinates: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: '请输入授课地点'
    },
    siteId: {
      type: String,
      default: ''
    },
    libraryEnabled: {
      type: Boolean,
      default: true
    },
    mapKey: {
      type: String,
      required: true
    },
    circleRadius: {
      type: Number,
      default: 500
    }
  },
  data () {
    return {
      currentAddress: '',
      showMap: false,
      searchKeyword: '',
      selectedAddress: '',
      selectedPosition: '',
      selectedCoordinates: '',
      map: null,
      marker: null,
      circle: null,
      geocoder: null,
      mapContainerId: `map-container-${Math.random().toString(36).substr(2, 9)}`,
      searchLoading: false,
      userRegion: null
    }
  },
  mounted () {
    this.currentAddress = this.value
    this.loadTencentMap().catch(error => {
      console.error('地图API加载失败:', error)
    })
  },
  watch: {
    value (newVal) {
      this.currentAddress = newVal
    }
  },
  methods: {
    loadTencentMap () {
      // 检查TMap是否已经加载
      if (window.TMap) {
        console.log('腾讯地图API已存在，跳过加载')
        return Promise.resolve()
      }

      // 检查是否已经有其他组件在加载API
      if (window.tencentMapLoading) {
        console.log('腾讯地图API正在加载中，等待加载完成')
        return window.tencentMapLoading
      }

      console.log('开始加载腾讯地图API')

      // 创建加载Promise并存储到全局，避免重复加载
      window.tencentMapLoading = new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = 'https://map.qq.com/api/gljs?v=1.exp&key=' + this.mapKey + '&libraries=service'
        script.async = true
        script.onload = () => {
          console.log('腾讯地图API加载成功')
          // 等待一下让service模块完全初始化
          setTimeout(() => {
            // 清除加载标识
            window.tencentMapLoading = null
            resolve()
          }, 500)
        }
        script.onerror = () => {
          console.error('腾讯地图API加载失败')
          // 清除加载标识
          window.tencentMapLoading = null
          this.$message.error('地图服务加载失败，请检查网络连接')
          reject(new Error('腾讯地图API加载失败'))
        }
        document.head.appendChild(script)
      })

      return window.tencentMapLoading
    },

    onAddressInput (value) {
      this.$emit('input', value)
      this.$emit('address-change', value, '')
      if (this.showMap) {
        this.selectedPosition = (value || '').trim()
        this.selectedAddress = ''
        this.selectedCoordinates = ''
      }
    },

    handleLibrarySelect (item) {
      if (!item) {
        return
      }
      // 地址库的地址名称就是排课时显示的授课地点，坐标单独用于签到。
      const address = (item.name || item.address || '').trim()
      const coordinates = (item.tencentMapLocation || '').trim()
      this.currentAddress = address
      this.$emit('input', address)
      this.$emit('address-change', address, coordinates)
    },

    openMapSelector () {
      if (this.showMap) {
        // 如果地图已经显示，不重复打开
        return
      }

      this.showMap = true
      this.searchKeyword = this.currentAddress
      this.$nextTick(() => {
        // 延迟初始化地图，确保DOM已渲染
        setTimeout(() => {
          this.initMapWithRetry()
        }, 100)
      })
    },

    closeMap () {
      this.showMap = false
      this.selectedAddress = ''
      this.selectedPosition = ''
      this.selectedCoordinates = ''

      // 清理地图资源
      if (this.marker) {
        try {
          this.marker.setMap(null)
        } catch (error) {
          console.warn('清理标记失败:', error)
        }
        this.marker = null
      }

      if (this.circle) {
        try {
          this.circle.destroy()
        } catch (error) {
          console.warn('清理圆圈失败:', error)
        }
        this.circle = null
      }

      if (this.map) {
        try {
          this.map.destroy()
        } catch (error) {
          console.warn('销毁地图失败:', error)
        }
        this.map = null
      }

      this.geocoder = null
    },

    initMapWithRetry (retryCount = 0) {
      const maxRetries = 5 // 减少重试次数

      if (window.TMap && window.TMap.service) {
        console.log('腾讯地图API和服务模块都已加载，开始初始化地图')
        this.initMap()
      } else if (window.TMap) {
        // TMap已加载但service模块可能还没完全初始化
        console.log('TMap已加载，等待service模块初始化...')
        if (retryCount < maxRetries) {
          setTimeout(() => {
            this.initMapWithRetry(retryCount + 1)
          }, 200) // 减少等待时间
        } else {
          console.warn('service模块初始化超时，尝试无service模式')
          this.initMap()
        }
      } else if (retryCount < maxRetries) {
        console.log(`等待腾讯地图API加载，重试次数: ${retryCount + 1}`)
        setTimeout(() => {
          this.initMapWithRetry(retryCount + 1)
        }, 300) // 减少等待时间
      } else {
        console.error('腾讯地图API加载超时')
        this.$message.error('地图服务加载超时，请刷新页面重试')
      }
    },

    initMap () {
      if (window.TMap) {
        try {
          // 默认中心点（可以根据当前输入的地址进行解析）
          let center = new window.TMap.LatLng(31.230416, 121.473701) // 上海

          const mapContainer = document.getElementById(this.mapContainerId)
          if (!mapContainer) {
            console.error('地图容器未找到:', this.mapContainerId)
            this.$message.error('地图容器初始化失败')
            return
          }

          this.map = new window.TMap.Map(mapContainer, {
            center: center,
            zoom: 13,
            pitch: 0,
            rotation: 0
          })

          // 检查service是否可用
          if (window.TMap.service && window.TMap.service.Geocoder) {
            this.geocoder = new window.TMap.service.Geocoder()
          } else {
            console.warn('腾讯地图地理编码服务不可用，将使用基础功能')
            this.geocoder = null
          }

          // 优先使用坐标定位，其次使用地址搜索
          if (this.coordinates && this.coordinates.trim()) {
            console.log('使用坐标定位:', this.coordinates)
            this.locateByCoordinates(this.coordinates.trim())
          } else if (this.currentAddress && this.currentAddress.trim()) {
            console.log('使用地址搜索:', this.currentAddress)
            this.searchKeyword = this.currentAddress
            this.searchLocation()
          }

          // 地图点击事件
          this.map.on('click', (evt) => {
            this.onMapClick(evt.latLng)
          })

          console.log('地图初始化成功')
        } catch (error) {
          console.error('地图初始化失败:', error)
          this.$message.error('地图初始化失败: ' + error.message)
        }
      } else {
        console.error('TMap未加载')
        this.$message.error('地图组件加载失败，请刷新页面重试')
      }
    },

    locateByCoordinates (coordinatesStr) {
      try {
        // 解析坐标字符串，支持多种格式
        const coords = this.parseCoordinates(coordinatesStr)
        if (!coords) {
          console.warn('坐标格式无效:', coordinatesStr)
          // 坐标无效时，尝试使用地址搜索
          if (this.currentAddress && this.currentAddress.trim()) {
            console.log('坐标无效，降级到地址搜索:', this.currentAddress)
            this.searchKeyword = this.currentAddress
            this.searchLocation()
          }
          return
        }

        console.log('解析坐标成功:', coords)
        // 创建坐标点
        const latLng = new window.TMap.LatLng(coords.lat, coords.lng)
        // 设置地图中心和缩放级别
        this.map.setCenter(latLng)
        this.map.setZoom(16)
        // 清除之前的标记
        if (this.marker) {
          this.marker.setMap(null)
        }

        // 添加新标记
        try {
          this.marker = new window.TMap.MultiMarker({
            id: 'selected-marker',
            map: this.map,
            geometries: [{
              id: 'marker1',
              styleId: 'marker',
              position: latLng
            }],
            styles: {
              marker: new window.TMap.MarkerStyle({
                width: 25,
                height: 35,
                anchor: { x: 16, y: 32 }
              })
            }
          })
        } catch (error) {
          console.warn('标记创建失败:', error)
        }

        // 添加500米圆圈
        this.addCircle(latLng)

        // 设置选中信息
        this.selectedCoordinates = coordinatesStr
        // 设置选中位置为用户输入的地址
        this.selectedPosition = this.currentAddress || '坐标定位'
        // 通过逆地理编码获取地址
        if (this.geocoder) {
          this.reverseGeocode(latLng)
        } else {
          this.selectedAddress = `位置 ${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`
        }
        console.log('坐标定位成功')
      } catch (error) {
        console.error('坐标定位失败:', error)
        // 坐标定位失败时，尝试使用地址搜索
        if (this.currentAddress && this.currentAddress.trim()) {
          console.log('坐标定位失败，降级到地址搜索:', this.currentAddress)
          this.searchKeyword = this.currentAddress
          this.searchLocation()
        }
      }
    },

    parseCoordinates (coordinatesStr) {
      if (!coordinatesStr || typeof coordinatesStr !== 'string') {
        return null
      }
      // 支持多种分隔符：逗号、空格、分号
      const cleanStr = coordinatesStr.trim()
      let parts = []
      if (cleanStr.includes(',')) {
        parts = cleanStr.split(',')
      } else if (cleanStr.includes(' ')) {
        parts = cleanStr.split(' ')
      } else if (cleanStr.includes(';')) {
        parts = cleanStr.split(';')
      } else {
        return null
      }
      if (parts.length !== 2) {
        return null
      }
      const lat = parseFloat(parts[0].trim())
      const lng = parseFloat(parts[1].trim())
      // 验证坐标范围
      if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        return null
      }
      return { lat, lng }
    },

    onMapClick (latLng) {
      this.selectedCoordinates = `${latLng.lat},${latLng.lng}`
      // 设置选中位置为用户搜索的地址（如果有的话）
      this.selectedPosition = (this.searchKeyword || '').trim() ||
        (this.currentAddress || '').trim() || '地图点击位置'

      // 清除之前的标记
      if (this.marker) {
        this.marker.setMap(null)
      }

      // 创建新标记
      try {
        this.marker = new window.TMap.MultiMarker({
          map: this.map,
          geometries: [{
            id: 'click-marker',
            position: latLng,
            properties: {
              title: '地图点击位置'
            }
          }],
          styles: {
            'default': new window.TMap.MarkerStyle({
              width: 25,
              height: 35,
              anchor: { x: 16, y: 32 }
            })
          }
        })
        console.log('地图点击标记创建成功')
      } catch (error) {
        console.warn('创建地图标记失败:', error)
      }

      // 添加500米圆圈
      this.addCircle(latLng)

      // 尝试逆地理编码获取地址，如果失败则使用坐标作为地址
      if (this.geocoder) {
        this.reverseGeocode(latLng)
      } else {
        console.log('地理编码服务不可用，直接使用坐标')
        this.selectedAddress = `位置 ${latLng.lat.toFixed(6)}, ${latLng.lng.toFixed(6)}`
      }
    },

    reverseGeocode (latLng) {
      // 使用后端接口进行逆地理编码，获取完整地址信息
      console.log('开始逆地理编码:', latLng.lat, latLng.lng)

      this.$ajax({
        url: '/support/thirdparty/tencent/map/reverseGeocoder',
        method: 'post',
        data: this.$qs.stringify({
          lat: latLng.lat,
          lng: latLng.lng,
          coordType: 5
        })
      }).then(response => {
        console.log('逆地理编码响应:', response.data)
        if (response.data && response.data.errorCode === 0) {
          const reverseGeocoderResponse = response.data.reverseGeocoderResponse
          if (reverseGeocoderResponse && reverseGeocoderResponse.status === 0) {
            const result = reverseGeocoderResponse.result
            if (result) {
              let fullAddress = ''
              // 优先使用formatted_addresses中的recommend（推荐地址）
              if (result.formatted_addresses && result.formatted_addresses.recommend) {
                fullAddress = result.formatted_addresses.recommend
                console.log('使用recommend地址:', fullAddress)
              } else if (result.formatted_addresses && result.formatted_addresses.complete) {
              // 如果没有推荐地址，使用complete（完整地址）
                fullAddress = result.formatted_addresses.complete
                console.log('使用complete地址:', fullAddress)
              } else if (result.address) {
              // 如果还没有，使用address字段
                fullAddress = result.address
                console.log('使用address字段:', fullAddress)
              } else if (result.address_component) {
              // 最后手动拼接详细地址组件
                const component = result.address_component
                console.log('手动拼接地址，组件:', component)
                const parts = []
                // 按层级拼接：省、市、区、街道、门牌号
                if (component.province && component.province !== component.city) {
                  parts.push(component.province)
                  console.log('添加省份:', component.province)
                }
                if (component.city) {
                  parts.push(component.city)
                  console.log('添加城市:', component.city)
                }
                if (component.district) {
                  parts.push(component.district)
                  console.log('添加区县:', component.district)
                }
                if (component.street) {
                  parts.push(component.street)
                  console.log('添加街道:', component.street)
                }
                if (component.street_number) {
                  parts.push(component.street_number)
                  console.log('添加门牌号:', component.street_number)
                }
                fullAddress = parts.join('')
                console.log('拼接后的完整地址:', fullAddress)
              }
              // 如果仍然没有获取到地址，使用坐标
              if (!fullAddress || fullAddress.trim() === '') {
                fullAddress = `位置 ${latLng.lat.toFixed(6)}, ${latLng.lng.toFixed(6)}`
                console.log('使用坐标作为地址:', fullAddress)
              }
              console.log('最终选择的地址:', fullAddress)
              this.selectedAddress = fullAddress
            } else {
              console.warn('逆地理编码返回无效结果')
              this.selectedAddress = `位置 ${latLng.lat.toFixed(6)}, ${latLng.lng.toFixed(6)}`
            }
          } else {
            console.warn('逆地理编码失败:', reverseGeocoderResponse.message || '未知错误')
            this.selectedAddress = `位置 ${latLng.lat.toFixed(6)}, ${latLng.lng.toFixed(6)}`
          }
        } else {
          console.warn('逆地理编码请求失败:', response.data.message || '未知错误')
          this.selectedAddress = `位置 ${latLng.lat.toFixed(6)}, ${latLng.lng.toFixed(6)}`
        }
      }).catch(error => {
        console.error('逆地理编码请求异常:', error)
        this.selectedAddress = `位置 ${latLng.lat.toFixed(6)}, ${latLng.lng.toFixed(6)}`
        // 不显示错误消息，因为用户可能只是想选择坐标
      })
    },

    searchLocation () {
      const keyword = this.searchKeyword
      if (!keyword || !keyword.trim()) {
        this.$message.warning('请输入搜索关键词')
        return
      }

      const trimmedKeyword = keyword.trim()
      console.log('开始搜索位置:', trimmedKeyword)
      this.selectedPosition = trimmedKeyword
      this.selectedAddress = ''
      this.selectedCoordinates = ''

      // 智能地址补全：如果地址不包含省市信息，尝试添加默认区域
      let searchAddress = this.enhanceAddress(trimmedKeyword)
      console.log('增强后的搜索地址:', searchAddress)

      this.searchLoading = true
      this.$ajax({
        url: '/support/thirdparty/tencent/map/geocoder',
        method: 'post',
        data: this.$qs.stringify({
          address: searchAddress,
          policy: 1 // 使用宽松策略，提高匹配成功率
        })
      }).then(response => {
        console.log('搜索响应:', response.data)
        if (response.data && response.data.errorCode === 0) {
          const geocoderResponse = response.data.geocoderResponse
          if (geocoderResponse && geocoderResponse.status === 0) {
            const result = geocoderResponse.result
            if (result && result.location) {
              const location = result.location
              const title = result.title || searchAddress
              console.log('搜索成功，位置:', location, '标题:', title)

              // 更新地图中心位置
              if (this.map) {
                const center = new window.TMap.LatLng(location.lat, location.lng)
                this.map.setCenter(center)
                this.map.setZoom(16)

                // 清除旧标记
                if (this.marker) {
                  this.marker.setMap(null)
                }

                // 添加新标记
                this.marker = new window.TMap.MultiMarker({
                  map: this.map,
                  geometries: [{
                    id: 'search-result',
                    position: center,
                    properties: {
                      title: title
                    }
                  }]
                })

                // 添加500米圆圈
                this.addCircle(center)

                // 更新坐标信息
                this.selectedCoordinates = `${location.lat},${location.lng}`
                console.log('更新选中坐标:', this.selectedCoordinates)

                // 调用逆地理编码获取完整地址信息
                this.reverseGeocode(center)
              }
            } else {
              // 如果增强地址搜索失败，尝试原始地址
              if (searchAddress !== trimmedKeyword) {
                console.log('增强地址搜索失败，尝试原始地址')
                this.searchWithFallback(trimmedKeyword)
              } else {
                this.$message.warning('未找到该地址的位置信息，请输入更详细的地址（如：上海市黄浦区XXX街道）')
              }
            }
          } else {
            // 如果增强地址搜索失败，尝试原始地址
            if (searchAddress !== trimmedKeyword) {
              console.log('增强地址搜索失败，尝试原始地址')
              this.searchWithFallback(trimmedKeyword)
            } else {
              this.$message.warning(geocoderResponse.message || '地址解析失败，请输入更详细的地址')
            }
          }
        } else {
          this.$message.warning(response.data.message || '搜索失败')
        }
      }).catch(error => {
        console.error('搜索地址失败:', error)
        this.$message.error('搜索失败，请稍后重试')
      }).finally(() => {
        this.searchLoading = false
      })
    },

    // 地址智能增强：添加默认区域信息
    enhanceAddress (address) {
      // 检查地址是否已经包含省市信息
      const hasProvince = /省|市|自治区|特别行政区/.test(address)
      const hasCity = /市|县|区|州|盟/.test(address)
      // 如果已经包含省市信息，直接返回
      if (hasProvince || hasCity) {
        return address
      }
      // 根据用户当前位置或默认区域进行地址增强
      // 这里可以根据实际需求设置默认区域，比如从用户信息中获取
      const defaultRegion = this.getDefaultRegion()
      if (defaultRegion) {
        return `${defaultRegion}${address}`
      }
      return address
    },

    // 获取默认区域（可以根据用户信息、IP定位等来确定）
    getDefaultRegion () {
      // 方案1: 从缓存中获取用户区域
      if (this.userRegion) {
        return this.userRegion
      }
      // 方案2: 从用户信息中获取（如果有的话）
      // if (this.$store.state.user.region) {
      //   return this.$store.state.user.region
      // }
      // 方案3: 使用项目默认区域（比如学校所在地）
      // 这里假设是上海地区，可以根据实际情况修改
      const defaultRegion = '上海市'
      // 方案4: 异步获取用户IP定位（不阻塞当前操作）
      this.getUserLocationAsync()
      return defaultRegion
    },

    // 异步获取用户当前位置
    getUserLocationAsync () {
      // 避免重复请求
      if (this.userRegion !== null) {
        return
      }
      // 设置为空字符串，表示正在获取中，避免重复请求
      this.userRegion = ''
      this.$ajax({
        url: '/support/thirdparty/tencent/map/ipLocation',
        method: 'post',
        data: this.$qs.stringify({})
      }).then(response => {
        console.log('IP定位响应:', response.data)
        if (response.data && response.data.errorCode === 0) {
          const ipLocationResponse = response.data.ipLocationResponse
          if (ipLocationResponse && ipLocationResponse.status === 0) {
            const result = ipLocationResponse.result
            if (result && result.ad_info) {
              const adInfo = result.ad_info
              // 构建用户区域：优先使用city，如果没有则使用province
              let region = ''
              if (adInfo.city && adInfo.city !== '未知') {
                region = adInfo.city
              } else if (adInfo.province && adInfo.province !== '未知') {
                region = adInfo.province
              }
              if (region) {
                this.userRegion = region
                console.log('获取到用户区域:', region)
              } else {
                this.userRegion = '上海市' // 降级到默认区域
              }
            } else {
              this.userRegion = '上海市' // 降级到默认区域
            }
          } else {
            this.userRegion = '上海市' // 降级到默认区域
          }
        } else {
          this.userRegion = '上海市' // 降级到默认区域
        }
      }).catch(error => {
        console.warn('IP定位失败:', error)
        this.userRegion = '上海市' // 降级到默认区域
      })
    },

    // 降级搜索：使用原始地址再次尝试
    searchWithFallback (originalAddress) {
      console.log('使用原始地址降级搜索:', originalAddress)
      this.$ajax({
        url: '/support/thirdparty/tencent/map/geocoder',
        method: 'post',
        data: this.$qs.stringify({
          address: originalAddress,
          policy: 1 // 使用宽松策略
        })
      }).then(response => {
        console.log('降级搜索响应:', response.data)
        if (response.data && response.data.errorCode === 0) {
          const geocoderResponse = response.data.geocoderResponse
          if (geocoderResponse && geocoderResponse.status === 0) {
            const result = geocoderResponse.result
            if (result && result.location) {
              const location = result.location
              const title = result.title || originalAddress
              console.log('降级搜索成功，位置:', location, '标题:', title)

              // 设置选中位置为用户搜索的关键词
              this.selectedPosition = originalAddress
              // 更新地图（与上面的逻辑相同）
              if (this.map) {
                const center = new window.TMap.LatLng(location.lat, location.lng)
                this.map.setCenter(center)
                this.map.setZoom(16)

                if (this.marker) {
                  this.marker.setMap(null)
                }

                this.marker = new window.TMap.MultiMarker({
                  map: this.map,
                  geometries: [{
                    id: 'search-result',
                    position: center,
                    properties: {
                      title: title
                    }
                  }]
                })

                // 添加500米圆圈
                this.addCircle(center)

                this.selectedCoordinates = `${location.lat},${location.lng}`
                this.reverseGeocode(center)
              }
            } else {
              this.$message.warning('未找到该地址的位置信息，请输入更详细的地址（如：上海市黄浦区XXX街道）')
            }
          } else {
            this.$message.warning('地址解析失败，请输入更详细的地址（包含省市区信息）')
          }
        } else {
          this.$message.warning('搜索失败，请输入更详细的地址')
        }
      }).catch(error => {
        console.error('降级搜索失败:', error)
        this.$message.warning('搜索失败，请输入更详细的地址')
      })
    },

    addCircle (position) {
      if (this.circle) {
        this.circle.destroy()
      }
      // 创建圆圈（半径由配置决定）
      this.circle = new window.TMap.MultiCircle({
        map: this.map,
        styles: {
          'circle': new window.TMap.CircleStyle({
            'showBorder': true,
            'borderColor': 'rgba(55, 119, 255, 0.8)',
            'borderWidth': 1,
            'fillColor': 'rgba(55, 119, 255, 0.3)'
          })
        },
        geometries: [{
          id: 'circle1',
          styleId: 'circle',
          center: position,
          radius: this.circleRadius // 圆圈半径由配置决定
        }]
      })
    },

    saveToLibrary () {
      if (!this.siteId) {
        this.$message.warning('请先选择教学点')
        return
      }
      const addressName = (this.selectedPosition || '').trim()
      const coordinates = (this.selectedCoordinates || '').trim()
      if (!addressName || !coordinates) {
        this.$message.warning('请先在地图上选择位置')
        return
      }
      this.$prompt('请输入便于识别的地址名称', '保存到地址库', {
        confirmButtonText: '保存',
        cancelButtonText: '取消',
        inputValue: addressName,
        inputPattern: /^.{1,100}$/,
        inputErrorMessage: '地址名称长度应为1到100个字符'
      }).then(({value}) => {
        const name = value ? value.trim() : ''
        if (!name) {
          this.$message.warning('地址名称不能为空')
          return
        }
        this.$ajax({
          url: '/workspace/manager/teaching/schedule/arrange/address/save',
          method: 'post',
          data: this.$qs.stringify({
            siteId: this.siteId,
            name,
            // 地址库的授课地点使用地址名称，地图坐标独立保存用于签到。
            address: name,
            tencentMapLocation: coordinates
          })
        }).then(response => {
          if (response.data && response.data.responseCode === 'SUCCESS') {
            this.$message.success(response.data.message)
            this.$emit('library-saved')
          } else if (response.data) {
            this.$message.warning(response.data.message)
          }
        }, () => {
          this.$message.error('签到地址保存失败，请稍后重试')
        })
      }).catch(() => {})
    },

    confirmSelection () {
      if (this.selectedPosition && this.selectedCoordinates) {
        // 排课授课地点使用搜索/输入文本，签到坐标使用地图选点坐标。
        this.currentAddress = this.selectedPosition
        this.$emit('input', this.selectedPosition)
        this.$emit('address-change', this.selectedPosition, this.selectedCoordinates)
        this.closeMap()
        this.$message.success('地址选择成功')
      } else if (this.selectedAddress && this.selectedCoordinates) {
        // 没有搜索文本时，保留逆地理编码结果作为兜底授课地点。
        this.currentAddress = this.selectedAddress
        this.$emit('input', this.selectedAddress)
        this.$emit('address-change', this.selectedAddress, this.selectedCoordinates)
        this.closeMap()
        this.$message.success('地址选择成功')
      } else {
        this.$message.warning('请先选择一个位置')
      }
    }
  },

  beforeDestroy () {
    if (this.map) {
      this.map.destroy()
    }
  }
}
</script>

<style lang="scss" scoped>
.address-selector {
  .address-input-row {
    display: flex;
    align-items: center;
    gap: 8px;

    > .el-input {
      flex: auto;
      min-width: 0;
    }

    > .address-library-selector-action {
      flex: none;
    }
  }

  .map-container {
    margin-top: 10px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background: #fff;

    .map-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 15px;
      border-bottom: 1px solid #ebeef5;
      background: #f5f7fa;

      .map-title {
        font-size: 14px;
        font-weight: 500;
        color: #303133;
      }
    }

    .map-content {
      .search-box {
        padding: 10px 15px;
        border-bottom: 1px solid #ebeef5;
      }

      .map-view {
        width: 100%;
        height: 300px;
      }

      .map-info {
        padding: 10px 15px;
        background: #fafafa;
        font-size: 12px;

        .selected-position {
          margin-bottom: 8px;
          padding: 8px;
          background: #e8f4fd;
          border-radius: 4px;
          border-left: 3px solid #409eff;

          .label {
            color: #409eff;
            font-weight: 500;
            margin-right: 5px;
          }

          .position {
            color: #303133;
            font-weight: 500;
          }
        }

        .label {
          color: #909399;
          margin-right: 5px;
        }

        .address {
          color: #303133;
        }

        .coordinates {
          margin-top: 5px;

          .coords {
            color: #606266;
            font-family: monospace;
          }
        }
      }
    }

    .map-footer {
      padding: 10px 15px;
      border-top: 1px solid #ebeef5;
      text-align: right;

      .el-button {
        margin-left: 10px;
      }
    }
  }
}
</style>
